import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { X, Clock, Users, Zap } from 'lucide-react';
import toast from 'react-hot-toast';

const LifetimeBanner = () => {
  const { user, API_BASE_URL } = useAuth();
  const [isVisible, setIsVisible] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isSoldOut, setSoldOut] = useState(false);
  const [memberCount, setMemberCount] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  // Cookie management functions
  const setCookie = (name, value, days) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
  };

  const getCookie = (name) => {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Initialize banner state
  useEffect(() => {
    const checkOfferStatus = async () => {
      try {
        // Check if offer was dismissed recently (4 hours) or purchased permanently
        const dismissed = getCookie('lifetime_offer_dismissed');
        const purchased = getCookie('lifetime_offer_purchased');
        
        if (purchased === 'true') {
          // Permanently hide if already purchased
          setIsVisible(false);
          return;
        }
        
        // If dismissed recently, check if 4 hours have passed
        if (dismissed === 'true') {
          // For now, don't show if recently dismissed
          // The cookie will expire after 4 hours automatically
          setIsVisible(false);
          return;
        }

        // Check current member count
        const response = await fetch(`${API_BASE_URL}/api/lifetime-offer/status`);
        if (response.ok) {
          const data = await response.json();
          setMemberCount(data.member_count || 0);
          
          if (data.member_count >= 72) {
            setSoldOut(true);
            setIsVisible(true);
            // Hide after 5 seconds if sold out, then dismiss for 1 day
            setTimeout(() => {
              setIsVisible(false);
              setCookie('lifetime_offer_dismissed', 'true', 1);
            }, 5000);
            return;
          }
        }

        // Check/set timer
        let offerExpiry = getCookie('lifetime_offer_expiry');
        
        if (!offerExpiry) {
          // First time seeing the offer - set 48 hour timer
          const expiryTime = new Date().getTime() + (48 * 60 * 60 * 1000);
          setCookie('lifetime_offer_expiry', expiryTime.toString(), 3);
          offerExpiry = expiryTime.toString();
        }

        const now = new Date().getTime();
        const timeRemaining = Math.max(0, Math.floor((parseInt(offerExpiry) - now) / 1000));
        
        if (timeRemaining > 0) {
          setTimeLeft(timeRemaining);
          setIsVisible(true);
        } else {
          // Offer expired - permanently dismiss
          setIsVisible(false);
          setCookie('lifetime_offer_dismissed', 'true', 365);
        }
      } catch (error) {
        console.error('Error checking offer status:', error);
      }
    };

    checkOfferStatus();
  }, [API_BASE_URL]);

  // Countdown timer
  useEffect(() => {
    if (timeLeft > 0 && isVisible && !isSoldOut) {
      const timer = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && isVisible && !isSoldOut) {
      // Timer expired
      setIsVisible(false);
      setCookie('lifetime_offer_dismissed', 'true', 365);
    }
  }, [timeLeft, isVisible, isSoldOut]);

  const handlePurchase = async () => {
    if (!user) {
      // For logged-out users, redirect to registration with lifetime offer context
      window.location.href = '/register?offer=lifetime';
      return;
    }

    setIsProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/lifetime-offer/purchase`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Mark as purchased to permanently hide banner
        setCookie('lifetime_offer_purchased', 'true', 365);
        
        // Redirect to checkout
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to initiate purchase');
      }
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error('Failed to process purchase');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    // Snooze for 4 hours, then show again to maximize conversion opportunities
    // This gives users multiple chances to see the offer during the 48-hour window
    setCookie('lifetime_offer_dismissed', 'true', 0.17); // 0.17 days = ~4 hours
  };

  if (!isVisible) return null;

  return (
    <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white relative z-50">
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Zap className="h-6 w-6 text-yellow-300 animate-pulse" />
            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4">
              <div className="font-bold text-lg">
                {isSoldOut ? (
                  "🔥 SOLD OUT - All 72 Lifetime Spots Claimed!"
                ) : (
                  "⚡ FOUNDER'S LIFETIME ACCESS - Never Pay Again!"
                )}
              </div>
              {!isSoldOut && (
                <div className="flex items-center space-x-4 text-sm">
                  <span className="bg-white/20 px-3 py-1 rounded-full">
                    $59 (Reg. $228/year)
                  </span>
                  <div className="flex items-center space-x-2">
                    <Clock className="h-4 w-4" />
                    <span className="font-mono font-bold">{formatTime(timeLeft)}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Users className="h-4 w-4" />
                    <span>{72 - memberCount} left</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            {!isSoldOut && (
              <button
                onClick={handlePurchase}
                disabled={isProcessing}
                className="bg-white text-orange-600 px-6 py-2 rounded-full font-bold hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                {isProcessing ? 'Processing...' : 'Claim Now'}
              </button>
            )}
            <button
              onClick={handleDismiss}
              className="text-white hover:text-gray-300 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LifetimeBanner;